import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import { ColorPicker } from 'element-ui'
import draggable from 'vuedraggable'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { RATING_ICON_OPTIONS, MIN_ICONS, MAX_ICONS, MIN_LEVELS, MAX_LEVELS, iconLabel, ratingPreviewValue } from '@/util/ratings'
import { tagChipStyle } from '@/util/tagChip'

interface LevelRow { key: number, name: string, previous_position: number | null }

export default /*#__PURE__*/_defineComponent({
  __name: 'RatingScaleDrawer',
  props: {
  show: { type: Boolean, default: false },
  scale: { type: Object, default: null },
},
  emits: ['update:show', 'close', 'saved'],
  setup(__props, { emit }) {

const props = __props





const { proxy } = getCurrentInstance() as any

// el-color-picker is not globally registered; register it for this template.
const ElColorPicker = ColorPicker

const displayOptions = [
  { value: 'icons', label: 'Icons' },
  { value: 'labels', label: 'Named levels' },
]
const iconOptions = RATING_ICON_OPTIONS

const isEdit = computed(() => !!(props.scale && props.scale.id))
const ratedCount = computed(() => Number(props.scale?.rated_event_count || 0))

let levelKey = 0

function newLevel(name = '', previous: number | null = null): LevelRow {
  levelKey += 1
  return { key: levelKey, name, previous_position: previous }
}

function defaultForm() {
  return {
    name: '',
    display_type: 'icons',
    max_value: 5,
    allow_half: false,
    icon: 'star',
    color: '#e14eca',
    levels: [newLevel('Beginner'), newLevel('Intermediate'), newLevel('Advanced')] as LevelRow[],
  }
}

const form = ref(defaultForm())
const errs = ref<Record<string, string>>({})
const saving = ref(false)
const dialog = ref({ show: false, title: '', message: '' })

const previewValue = computed(() => ratingPreviewValue({ max_value: form.value.max_value, allow_half: form.value.allow_half ? 1 : 0 }))

const previewScale = computed(() => ({
  display_type: 'icons',
  max_value: form.value.max_value,
  icon: form.value.icon,
  color: form.value.color,
}))

function loadForm() {
  errs.value = {}
  const s = props.scale
  if (s && s.id) {
    form.value = {
      name: s.name || '',
      display_type: s.display_type,
      max_value: s.display_type === 'icons' ? Number(s.max_value) : 5,
      allow_half: !!s.allow_half,
      icon: s.icon || 'star',
      color: s.color || '#e14eca',
      levels: s.display_type === 'labels' && s.levels.length
        ? s.levels.map((name: string, i: number) => newLevel(name, i + 1))
        : defaultForm().levels,
    }
  } else {
    form.value = defaultForm()
  }
}

function removeLevel(index: number) {
  form.value.levels = form.value.levels.filter((_, i) => i !== index)
}

function addLevel() {
  form.value.levels = [...form.value.levels, newLevel()]
}

function validate() {
  const e: Record<string, string> = {}
  if (!form.value.name.trim()) e.name = 'Name is required'
  if (form.value.display_type === 'labels') {
    const seen = new Set<string>()
    form.value.levels.forEach((level, i) => {
      const name = level.name.trim()
      if (!name) e['levels[' + i + ']'] = 'Level name is required'
      else if (seen.has(name.toLowerCase())) e['levels[' + i + ']'] = 'Level names must be unique'
      seen.add(name.toLowerCase())
    })
    if (form.value.levels.length < MIN_LEVELS || form.value.levels.length > MAX_LEVELS) {
      e.levels = `Add between ${MIN_LEVELS} and ${MAX_LEVELS} levels`
    }
  }
  errs.value = e
  return Object.keys(e).length === 0
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function baseUrl() {
  const companyId = localStorage.getItem('companyId')
  const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
  return `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/rating-scales`
}

function errorBody(err: any) {
  const data = err?.response?.data
  if (typeof data === 'string') {
    try { return JSON.parse(data) } catch (e) { return { error: data } }
  }
  return data || {}
}

async function save() {
  if (!validate()) return
  saving.value = true
  try {
    const config = await authConfig()
    const isLabels = form.value.display_type === 'labels'
    const payload: Record<string, any> = {
      name: form.value.name.trim(),
      display_type: form.value.display_type,
      max_value: isLabels ? form.value.levels.length : form.value.max_value,
      allow_half: !isLabels && form.value.allow_half ? 1 : 0,
      icon: form.value.icon,
      color: form.value.color,
    }
    if (isLabels) {
      payload.levels = form.value.levels.map(l => ({ name: l.name.trim(), previous_position: l.previous_position }))
    }
    if (isEdit.value) {
      payload.archived = props.scale.archived ? 1 : 0
      await axios.put(`${baseUrl()}/${props.scale.id}`, payload, config)
    } else {
      await axios.post(baseUrl(), payload, config)
    }
    proxy.$notify({ type: 'success', message: isEdit.value ? 'Rating scale saved' : 'Rating scale added', icon: 'tim-icons icon-check-2' })
    emit('saved')
    onClose()
  } catch (err: any) {
    const status = err?.response?.status
    const body = errorBody(err)
    if (status === 409) {
      const count = Number(body.in_use_conflict_count || 0)
      dialog.value = {
        show: true,
        title: 'Ratings in use',
        message: `${count} ${count === 1 ? 'event uses' : 'events use'} a rating this change would remove. Update those events first, or keep the current setting.`,
      }
    } else if (status === 400 && body.validation_errors) {
      const { scales, ...fields } = body.validation_errors
      errs.value = fields
      if (scales) dialog.value = { show: true, title: 'Could not save', message: scales }
    } else {
      dialog.value = { show: true, title: 'Could not save', message: 'The rating scale could not be saved. Please try again.' }
    }
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && !dialog.value.show) onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadForm()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,props, emit, proxy, ElColorPicker, displayOptions, iconOptions, isEdit, ratedCount, levelKey, newLevel, defaultForm, form, errs, saving, dialog, previewValue, previewScale, loadForm, removeLevel, addLevel, validate, authConfig, baseUrl, errorBody, save, onClose, onEscKey, draggable, MIN_ICONS, MAX_ICONS, MIN_LEVELS, MAX_LEVELS, iconLabel, tagChipStyle }
}

})